import React from "react";
import PropTypes from "prop-types";

const StagedRun = ({ run, onProceed, onCancel, onDownload, busy, dryRunComplete }) => {
  const counts = run.counts || {};
  const actions = run.actions || {};
  const artifacts = run.artifacts || [];
  const passing = artifacts.find(artifact => artifact.type === "passing");
  const failing = artifacts.find(artifact => artifact.type === "failures");
  const passingRows = (counts.create || 0) + (counts.update || 0);
  const allRowsFailed = dryRunComplete && passingRows === 0 && counts.positions > 0;
  const noRowsToValidate = counts.positions === 0;
  let note = (
    <p className="import-note">
      Only the
      {` ${passingRows} passing rows will be submitted for import. Failed rows will not be imported.`}
    </p>
  );
  if (noRowsToValidate) {
    note = (
      <p className="import-note" role="status">
        No rows were found to validate. Upload a workbook with property rows to run a dry run.
      </p>
    );
  }
  if (allRowsFailed) {
    note = (
      <p className="import-note" role="status">
        No rows passed dry-run validation. Review the failing file or cancel this import.
      </p>
    );
  }
  const downloads = [
    ["Passing file", passing, passingRows, "passing", "Download passing file", "passing"],
    ["Failing file", failing, counts.dryRunFail || 0, "failures", "Download failing file", "failing"],
  ];

  return (
    <div className="import-workspace import-staged" data-testid="import-staged">
      <h3>{dryRunComplete ? "Dry run complete" : "Dry run results"}</h3>
      <p className="import-file-meta">{run.filename}</p>
      {!allRowsFailed && (
        <div className="import-stat import-stat-primary">
          <span>Passing rows available to import</span>
          <strong>{passingRows}</strong>
        </div>
      )}
      <div className="import-artifact-grid">
        {downloads.map(
          ([label, artifact, rows, type, buttonLabel, outcome]) =>
            artifact && (
              <section className="import-artifact-card" key={type}>
                <span className="import-file-icon" aria-hidden="true">
                  <svg viewBox="0 0 32 40" focusable="false">
                    <path d="M4 1.5h15l9 9V38.5H4z" />
                    <path d="M19 1.5v9h9" />
                  </svg>
                </span>
                <div className="import-artifact-content">
                  <h4>{label}</h4>
                  <p>
                    {rows}
                    {` ${outcome} ${rows === 1 ? "row" : "rows"}`}
                  </p>
                  <button type="button" disabled={busy} onClick={() => onDownload(artifact.type)}>
                    {buttonLabel}
                  </button>
                </div>
              </section>
            ),
        )}
      </div>
      {note}
      {actions.canCancel && (
        <div className="import-info" role="note">
          <span className="import-info-icon" aria-hidden="true">
            i
          </span>
          <span>
            {noRowsToValidate
              ? "Download any files you want to keep before selecting Cancel. These downloads will no longer be available afterward."
              : "Download any files you want to keep before selecting Proceed or Cancel. These downloads will no longer be available afterward."}
          </span>
        </div>
      )}
      {actions.canCancel && (
        <div className="import-actions">
          <div className="import-action-buttons">
            <button
              type="button"
              className="import-button-secondary"
              disabled={!actions.canCancel || busy}
              onClick={onCancel}
            >
              Cancel
            </button>
            {!noRowsToValidate && actions.canProceed && (
              <button
                type="button"
                className="import-button-primary"
                data-testid="proceed-import"
                disabled={!actions.canProceed || busy}
                onClick={onProceed}
              >
                Proceed
              </button>
            )}
          </div>
          <small>Cancel imports no rows and returns you to the upload screen.</small>
        </div>
      )}
    </div>
  );
};
StagedRun.propTypes = {
  run: PropTypes.shape({
    filename: PropTypes.string,
    counts: PropTypes.object,
    artifacts: PropTypes.array,
    actions: PropTypes.object,
  }).isRequired,
  onProceed: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  dryRunComplete: PropTypes.bool,
};
StagedRun.defaultProps = { busy: false, dryRunComplete: false };
export default StagedRun;
